/* eslint-disable no-unused-vars */
import React, {
  HTMLInputTypeAttribute,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import * as yup from 'yup'

import { Form, FormButtons, OptionSelector, TextInput } from '@/components'
import { useForm } from '@/hooks'
import { t } from '@/i18n'
import { ICustomer } from '@/types'

import UserSelector from '@/pages/User/userSelector'
import { useBankSelect } from '../Cars/components/useBankSelect'
import { getIcon } from './utils'

interface IInput {
  name?: string
  label: string
  required?: boolean
  editable?: boolean
  className?: string
  emptyValue?: string
  checked?: boolean
  value?: any
  type: HTMLInputTypeAttribute
}

interface Props {
  loading: boolean
  isEditing?: boolean
  submitting: boolean
  onCancel: () => void
  details: Record<string, IInput[]>
  initialData?: Partial<ICustomer>
  onSubmit: (d: ICustomer) => void
}

const CustomerForm: React.FC<Props> = ({
  details,
  loading,
  onCancel,
  onSubmit,
  isEditing,
  submitting,
  initialData,
}) => {
  const schema: any = useMemo(
    () =>
      yup
        .object({
          name: yup.string().required(t('required_field_error')),
        })
        .required(),
    [],
  )

  console.log('initialData', initialData, 'details', details)

  const { control, isValid, onTriggerChange, onFormSubmit } = useForm<any>({
    isEditing,
    defaultValues: initialData,
    schema,
    onSubmit,
  })

  const formRef = useRef<HTMLFormElement | null>(null)

  const {
    loadingBanks,
    bankOptions,
    banksPageInfo,
    onFetchMore: onFetchBanksMore,
  } = useBankSelect({
    loading,
    initialBank: initialData?.bank,
  })

  useEffect(() => {
    if (initialData?.bank) {
      onTriggerChange('bank', initialData.bank.id)
    }
  }, [initialData?.bank, onTriggerChange])

  useEffect(() => {
    if (initialData?.accountManager) {
      onTriggerChange('accountManager', initialData.accountManager.id)
    }
  }, [initialData?.accountManager, onTriggerChange])

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  return (
    <Form<ICustomer>
      formRef={formRef}
      control={control}
      isEditing={isEditing}
      onSubmit={onFormSubmit}
    >
      {Object.keys(details || {}).map(key => (
        <div className="info-box w-100" key={key}>
          <div className="main-info">
            <div className="title-box">
              <h3>
                <span className={getIcon(key)}></span>
                {t(key)}
              </h3>
            </div>
            <div className="data-box">
              <div className="form-fields">
                {key === 'financial_details' && (
                  <OptionSelector
                    required
                    key={key}
                    name="bank"
                    className="w-26"
                    label={t('bank')}
                    control={control}
                    options={bankOptions}
                    menuPlacement="bottom"
                    emptyValue={emptyValue}
                    value={initialData?.bank?.id}
                    optionsLoading={loadingBanks}
                    disabled={!Boolean(isEditing)}
                    fieldValue={initialData?.bank?.id}
                    onFetchMore={() => onFetchBanksMore()}
                    fetchMoreStatus={banksPageInfo.fetchMoreStatus}
                  />
                )}
                {details[key].map(input => {
                  if (input.type === 'checkbox') {
                    input.checked = Boolean(input?.value)
                  }
                  if ((input.editable === false && isEditing) || !input.name) {
                    return null
                  }
                  const withoutValue = { ...input }
                  delete withoutValue.value
                  // @ts-ignore
                  return (
                    <TextInput
                      {...withoutValue}
                      key={input.name}
                      control={control}
                      label={t(input.label)}
                      emptyValue={emptyValue}
                      disabled={!Boolean(isEditing)}
                    />
                  )
                })}
                {key === 'general_info' && (
                  <UserSelector
                    isFormElement
                    control={control}
                    key={key}
                    className="w-26"
                    name="accountManager"
                    label={t('accountManager')}
                    isEditing={isEditing}
                    value={initialData?.accountManager?.id}
                    initial={initialData?.accountManager}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      <FormButtons
        formRef={formRef}
        visible={isEditing}
        onCancel={onCancel}
        isFormValid={isValid}
        submitting={submitting}
      />
    </Form>
  )
}

export default CustomerForm
